import { Dispatch } from 'redux';

import { Events } from 'src/constants/webSocketConsts/events';
import { ApiTags } from 'src/services/api/appApi';
import {
  FileChannelApiResponse,
  fileChannelsApi,
  ListFileChannelsParams,
  ListFileChannelsResponse,
} from 'src/services/api/fileChannelsApi';
import {
  addFileChannelsAction,
  removeFileChannelsAction,
  updateFileChannelsAction,
} from 'src/store/files/actions';
import { FileChannel } from 'src/store/files/types';
import { RootState } from 'src/store/reduxTypes';
import { ensureUnreachable } from 'src/utils/common_utils';

type MembershipType = 'individual' | 'company' | 'group';

/**
 * Type guard to check if a value is a valid membership type
 */
const isMembershipType = (
  value: string | undefined,
): value is MembershipType => {
  return value === 'individual' || value === 'company' || value === 'group';
};

/**
 * Convert legacy FileChannel format to FileChannelApiResponse format
 * Used for dispatching WebSocket updates to RTK Query cache
 */
const convertToApiFormat = (channel: FileChannel): FileChannelApiResponse => {
  const membershipType = isMembershipType(channel.membershipType)
    ? channel.membershipType
    : 'individual'; // Default fallback

  return {
    id: channel.id,
    object: 'fileChannel',
    identityId: channel.identityId,
    createdAt: channel.createdDate,
    updatedAt: channel.updatedDate,
    membershipType,
    membershipEntityId:
      membershipType === 'individual'
        ? channel.fields?.singleClientId || ''
        : channel.fields?.companyID || '',
    companyId: channel.fields?.companyID || '',
    memberIds: Object.keys(channel.additionalFields?.memberIDs || {}),
    rootClientPermissions: channel.rootClientPermissions,
    lastNotificationTimestamp:
      channel.fields?.lastNotification?.createdDate ?? channel.createdDate,
    lastNotificationSenderId:
      channel.fields?.lastNotification?.fields?.senderUserId,
  };
};

/**
 * Update RTK Query cache for all cached file channel queries
 * Uses selectInvalidatedBy to find all cached pages tagged with fileChannels
 */
const updateAllCachedPages = (
  event: Events,
  items: FileChannel[],
  dispatch: Dispatch<any>,
  getState: () => RootState,
) => {
  const convertedItems = items.map(convertToApiFormat);
  const state = getState();

  // Find all cached queries tagged with ApiTags.fileChannels
  const affected = fileChannelsApi.util.selectInvalidatedBy(state, [
    ApiTags.fileChannels,
  ]);

  // Update each cached query
  affected.forEach(({ endpointName, originalArgs }) => {
    // Only update listFileChannels queries (not getCompanyFileChannels)
    if (endpointName !== 'listFileChannels') return;

    const args = originalArgs as ListFileChannelsParams & {
      includeExtraData?: boolean;
    };

    dispatch(
      fileChannelsApi.util.updateQueryData(
        'listFileChannels',
        args,
        (draft: ListFileChannelsResponse) => {
          if (!draft?.data) return;

          convertedItems.forEach((channel) => {
            const existingIndex = draft.data.findIndex(
              (c) => c.id === channel.id,
            );

            switch (event) {
              case Events.Insert:
                // Add to first page only (the one without nextToken)
                if (!args.nextToken && existingIndex === -1) {
                  draft.data.unshift(channel);
                }
                break;
              case Events.Modify:
                if (existingIndex !== -1) {
                  // Update existing channel in this page
                  draft.data[existingIndex] = channel;
                }
                // Also add to first page if not already there (activity moves it to top)
                if (!args.nextToken && existingIndex === -1) {
                  draft.data.unshift(channel);
                }
                break;
              case Events.Remove:
                if (existingIndex !== -1) {
                  draft.data.splice(existingIndex, 1);
                }
                break;
            }
          });
        },
      ),
    );
  });
};

/**
 * Handle WebSocket events for file channels
 * Returns a thunk to access both dispatch and getState for RTK Query cache updates
 */
const ChannelHandler = (event: Events, items: FileChannel[]) => {
  return (dispatch: Dispatch<any>, getState: () => RootState) => {
    // Update RTK Query cache for new FilesSidebar
    updateAllCachedPages(event, items, dispatch, getState);

    // Dispatch legacy Redux action for backwards compatibility
    let legacyAction;
    switch (event) {
      case Events.Insert:
        legacyAction = addFileChannelsAction(items);
        break;
      case Events.Modify:
        legacyAction = updateFileChannelsAction(items);
        break;
      case Events.Remove:
        legacyAction = removeFileChannelsAction(items);
        break;
      case Events.DomainVerified:
      case Events.Refresh:
        return;
      default:
        ensureUnreachable(event);
    }
    if (legacyAction) {
      dispatch(legacyAction);
    }
  };
};

export default ChannelHandler;
